import * as React from 'react';

import { Button, Dialog } from '@coursera/cds-core';
import type { DialogProps } from '@coursera/cds-core';

import TrackProgressButton from 'bundles/degree-home/components/v3-dpm/components/TrackProgressCTA/TrackProgressButton';
import PlanUpdatedDialog from 'bundles/degree-home/components/v3-dpm/dialogs/PlanUpdatedDialog';
import TrackProgressTrialDisclaimerDialog from 'bundles/degree-home/components/v3-dpm/dialogs/TrackProgressTrialDisclaimerDialog';
import WhereToGoFromHereDialog from 'bundles/degree-home/components/v3-dpm/dialogs/WhereToGoFromHereDialog';

import _t from 'i18n!nls/degree-home';

const { Actions, HeadingGroup, Content } = Dialog;

export type DialogType =
  | 'whereToGoFromHere'
  | 'trackProgressTrialDisclaimer'
  | 'learnerHomeTrialDisclaimer'
  | 'planUpdated';

type DPMDialogContextType = {
  openDialog: (type: DialogType, degreeHomePagePath?: string) => void;
};

const dialogContentMap: Record<DialogType, React.FC> = {
  whereToGoFromHere: WhereToGoFromHereDialog,
  trackProgressTrialDisclaimer: TrackProgressTrialDisclaimerDialog,
  learnerHomeTrialDisclaimer: TrackProgressTrialDisclaimerDialog,
  planUpdated: PlanUpdatedDialog,
};

const DPMDialogContext = React.createContext<DPMDialogContextType>({ openDialog: () => undefined });

export function useDPMDialog() {
  const context = React.useContext(DPMDialogContext);
  if (context === undefined) {
    throw new Error('useDPMDialog must be used within a DPMDialogContext.Provider');
  }
  return context;
}

type DPMDialogProps = {
  children: React.ReactNode;
};

const getDialogTitleSupportTextMap = (): { [type in DialogType]: { title: string; supportText: string } } => {
  return {
    whereToGoFromHere: { title: _t('Where to go from here?'), supportText: '' },
    trackProgressTrialDisclaimer: { title: _t('Student Homepage'), supportText: _t('Trial disclaimer') },
    learnerHomeTrialDisclaimer: { title: _t('Student Homepage'), supportText: _t('Trial disclaimer') },
    planUpdated: { title: 'Your plan was automatically updated', supportText: '' },
  };
};

const getDialogColor = (dialogType: DialogType): DialogProps['color'] => {
  switch (dialogType) {
    case 'planUpdated':
      return 'white';
    case 'whereToGoFromHere':
    case 'learnerHomeTrialDisclaimer':
    case 'trackProgressTrialDisclaimer':
    default:
      return 'darkBlue';
  }
};

export const WithDPMDialog: React.VFC<DPMDialogProps> = ({ children }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogType, setDialogType] = React.useState<DialogType | null>(null);
  const [degreePath, setDegreePath] = React.useState('');

  const openDialog = React.useCallback((type: DialogType, degreeHomePagePath?: string) => {
    if (degreeHomePagePath) {
      setDegreePath(degreeHomePagePath);
    }

    setDialogType(type);
    setDialogOpen(true);
  }, []);

  const closeDialog = React.useCallback(() => {
    setDialogOpen(false);
  }, []);

  const DialogContentComponent = dialogType ? dialogContentMap[dialogType] : null;
  const dialogContent = DialogContentComponent ? <DialogContentComponent /> : null;
  const dialogTitleSupportTextMap = getDialogTitleSupportTextMap();
  const dialogTitle = dialogType ? dialogTitleSupportTextMap[dialogType].title : '';
  const dialogHeaderSupportText = dialogType ? dialogTitleSupportTextMap[dialogType].supportText : '';
  const dialogColor = dialogType ? getDialogColor(dialogType) : 'darkBlue';

  const getActionButton = () => {
    switch (dialogType) {
      case 'trackProgressTrialDisclaimer':
        return <TrackProgressButton size="medium" degreeHomePagePath={degreePath} />;
      case 'whereToGoFromHere':
      case 'learnerHomeTrialDisclaimer':
      case 'planUpdated':
      default:
        return (
          <Button type="button" size="medium" variant="primary" onClick={closeDialog}>
            {_t('OK')}
          </Button>
        );
    }
  };

  return (
    <DPMDialogContext.Provider value={{ openDialog }}>
      {children}
      <Dialog open={dialogOpen} onClose={closeDialog} color={dialogColor}>
        <HeadingGroup supportText={dialogHeaderSupportText}>{dialogTitle}</HeadingGroup>
        <Content>{dialogContent}</Content>
        <Actions>{getActionButton()}</Actions>
      </Dialog>
    </DPMDialogContext.Provider>
  );
};

export default DPMDialogContext;
